// Eskom PARCC — Findings Register (read-only, admin only).
//
// The monolith's Findings Register screen, rebuilt as a filterable/sortable
// table over the full findings population, each finding joined to its
// transaction (by `referenceNo`) so the dataset/division/category it inherits
// can be shown and filtered. Compliance is the finding's `finalCompliance ??
// suggestedCompliance`; status is `effectiveStatus ?? legacyStatus`. Filtering
// is client-side via the pure `filterFindings` helper; the DataTable owns sort.
//
// The Reference cell is a click-through: it links to the Transactions register
// with that reference pre-selected (`/transactions?ref=<referenceNo>`), so an
// auditor can jump from a finding line straight to its transaction detail.
//
// Read-only — NO mutations, NO edit controls, NO exports (those are Phase 3/4).
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DataTable,
  Badge,
  Button,
  ComplianceBadge,
  LoadingState,
  ErrorState,
  EmptyState,
  StatusIcon,
  StatusLegend,
  Truncated,
  Pager,
  usePagination,
  useToast,
  type Column,
} from '../../components/ui';
import { useFindings, useTransactions, useCesRows, useWorkingPapers } from '../../hooks';
import { unionOptions } from '../../lib/options';
import {
  buildFindingsCsv,
  buildFindingsSheet,
  buildBackupJson,
  downloadText,
  workbookFromSheet,
  downloadWorkbook,
} from '../exports';
import {
  DATASET_OPTIONS,
  DIVISION_OPTIONS,
  ALL_COMPLIANCE_LEVELS,
  ANCHORS,
  REVIEW_STATUS_OPTIONS,
} from '../../domain/enums';
import { canonicalDivision } from '../../domain/divisions';
import { DATASET_CATEGORIES } from '../dashboard/derive';
import type { Transaction } from '../../data/types';
import type { FindingRead } from '../../lib/db';
import type { RuleReferences } from '../../domain/rulebook/ruleReferences';
import {
  filterFindings,
  distinctFindingValues,
  effectiveFindingCompliance,
  deriveFindingReviewedState,
  EMPTY_FINDING_FILTERS,
  type FindingFilters as Filters,
  type TxByRef,
} from './filterFindings';
import { genuineFindings, findingDisposition } from '../../domain/findings';
import { isSelfDeclared } from '../../domain/gaps';
import { splitFindingDisplay } from './findingDisplay';
import { normalizeFindingStatus } from '../../domain/normalize';
import { FindingFilters } from './FindingFilters';
import { useVariant } from '../../lib/uiPrefs';

/**
 * The seven columns the redesign leads with. The remaining six (Review,
 * Confidence, Matched rule theme, Regulatory References, Rationale, Updated) are
 * still rendered — behind a "More columns" toggle — so the register never LOSES
 * a column, it only stops showing all thirteen at once by default.
 */
const PRIMARY_COLUMNS = [
  'reference',
  'division',
  'finding',
  'route',
  'status',
  'compliance',
  'anchors',
];

/** The finding's lifecycle status: effective ?? legacy, folded onto the
 *  four-state vocabulary (Open / In Progress / Closed / Closed by
 *  Self-Declaration) agreed on the 9 Jun alignment call. */
function findingStatus(f: FindingRead): string {
  return normalizeFindingStatus(f.effectiveStatus ?? f.legacyStatus ?? '');
}

/** Route, monolith-style: dashes → spaces, upper-cased (blank stays blank). */
function routeLabel(route: string | undefined): string {
  return (route ?? '').replace(/-/g, ' ').trim().toUpperCase();
}

/** Flatten the rulebook references into one cell: "Eskom: …; PFMA: …; Law: …". */
function flattenRefsText(refs: RuleReferences | undefined): string {
  if (!refs) return '';
  return [
    refs.eskomPolicy?.length ? `Eskom: ${refs.eskomPolicy.join('; ')}` : '',
    refs.pfma?.length ? `PFMA: ${refs.pfma.join('; ')}` : '',
    refs.law?.length ? `Law: ${refs.law.join('; ')}` : '',
  ]
    .filter(Boolean)
    .join(' | ');
}

/** The register's default scope — outstanding findings only (Lindiwe R9). */
const OUTSTANDING_FILTERS: Filters = { ...EMPTY_FINDING_FILTERS, disposition: 'outstanding' };

export function FindingsPage(): React.JSX.Element {
  const findingsQuery = useFindings();
  const transactionsQuery = useTransactions();
  // CES rows + working papers are loaded only so the JSON backup can snapshot the
  // full typed dataset (they are not used by the register table itself).
  const cesRowsQuery = useCesRows();
  const workingPapersQuery = useWorkingPapers();
  const { show } = useToast();
  // Column disclosure. Lumen leads with the seven columns the redesign calls for;
  // Legacy keeps showing all thirteen, exactly as it always has. Either way BOTH
  // sets are one button away, so no column is ever lost — only the default
  // differs, which is the whole shape of the Legacy/Lumen split.
  const variant = useVariant();
  const [showAllColumns, setShowAllColumns] = useState(variant !== 'lumen');
  // The register opens on the OUTSTANDING findings — the work still to do — so a
  // self-declared or resolved line does not read as still open (Lindiwe R9). "All
  // findings" (and the two closure routes) stay one select away. Reset returns here.
  const [filters, setFilters] = useState<Filters>(OUTSTANDING_FILTERS);

  // The register lists GENUINE findings — one per distinct non-compliant criterion
  // (Compliant / Not-Applicable / Pending lines, "Additional comments" notes and
  // duplicate rows all excluded), so the count, filter options and exports match the
  // dashboard + reports exactly. `genuineFindings` is the one shared definition.
  const findings = useMemo(() => genuineFindings(findingsQuery.data ?? []), [findingsQuery.data]);

  // Join key: a transaction per (non-deleted) referenceNo, so each finding row
  // resolves its dataset/division/category in O(1) without a per-row scan.
  const txByRef: TxByRef = useMemo(() => {
    const map = new Map<string, Transaction>();
    for (const t of transactionsQuery.data ?? []) {
      if (t.deleted || !t.referenceNo) continue;
      if (!map.has(t.referenceNo)) map.set(t.referenceNo, t);
    }
    return map;
  }, [transactionsQuery.data]);

  // Option lists: enum reference sets unioned with the distinct values present
  // across the joined transactions / findings.
  const datasetOptions = useMemo(
    () =>
      unionOptions(
        DATASET_OPTIONS,
        [...txByRef.values()].map((t) => t.dataset).filter((v): v is string => Boolean(v)),
      ),
    [txByRef],
  );
  const divisionOptions = useMemo(
    () =>
      unionOptions(
        DIVISION_OPTIONS,
        // Canonical so raw spelling variants collapse to one option, matching the
        // canonical filter in filterFindings (owner feedback Aug 2026).
        [...txByRef.values()].map((t) => canonicalDivision(t.division)).filter(Boolean),
      ),
    [txByRef],
  );
  const complianceOptions = useMemo(
    () =>
      unionOptions(
        ALL_COMPLIANCE_LEVELS,
        distinctFindingValues(findings, effectiveFindingCompliance),
      ),
    [findings],
  );
  const anchorOptions = useMemo(() => {
    const present = new Set<string>();
    for (const f of findings) for (const a of f.anchors ?? []) present.add(a);
    return unionOptions(ANCHORS, [...present]);
  }, [findings]);

  const filtered = useMemo(
    () => filterFindings(findings, txByRef, filters),
    [findings, txByRef, filters],
  );

  // The disposition split of the findings in scope (every active filter EXCEPT
  // disposition), so the count line reconciles the three routes regardless of which
  // disposition is being viewed: identified = outstanding + self-declared + resolved.
  const split = useMemo(() => {
    const inScope = filterFindings(findings, txByRef, { ...filters, disposition: '' });
    let outstanding = 0;
    let selfDeclared = 0;
    let resolved = 0;
    for (const f of inScope) {
      const tx = f.referenceNo ? txByRef.get(f.referenceNo) : undefined;
      const d = findingDisposition(f, tx ? isSelfDeclared(tx) : false);
      if (d === 'outstanding') outstanding += 1;
      else if (d === 'self-declared') selfDeclared += 1;
      else resolved += 1;
    }
    return { identified: inScope.length, outstanding, selfDeclared, resolved };
  }, [findings, txByRef, filters]);

  // Export the *currently-filtered* findings as the register CSV (monolith
  // parity). Build → download → toast; a failure is caught so the page never
  // crashes. Both exports are admin-only via the route gate on this screen.
  const exportCsv = useCallback(() => {
    try {
      const csv = buildFindingsCsv(filtered, txByRef);
      downloadText('procurement_findings_register.csv', csv, 'text/csv;charset=utf-8');
      show(`Exported ${filtered.length.toLocaleString('en-ZA')} finding(s) to CSV.`, 'success');
    } catch (err) {
      show(
        `Could not export the findings CSV: ${err instanceof Error ? err.message : String(err)}`,
        'error',
      );
    }
  }, [filtered, txByRef, show]);

  // Excel export of the currently-filtered register (owner feedback 2). Reuses the
  // shared column model, so the workbook matches the CSV column-for-column.
  const exportExcel = useCallback(() => {
    try {
      const sheet = buildFindingsSheet(filtered, txByRef);
      downloadWorkbook(
        'procurement_findings_register.xlsx',
        workbookFromSheet('Findings Register', sheet),
      );
      show(`Exported ${filtered.length.toLocaleString('en-ZA')} finding(s) to Excel.`, 'success');
    } catch (err) {
      show(
        `Could not export the findings Excel: ${err instanceof Error ? err.message : String(err)}`,
        'error',
      );
    }
  }, [filtered, txByRef, show]);

  // Export a clean typed snapshot of the live data as JSON. The timestamp is
  // supplied here (the pure builder never reads the clock).
  const exportBackup = useCallback(() => {
    try {
      const json = buildBackupJson(new Date().toISOString(), {
        transactions: transactionsQuery.data ?? [],
        findings: findingsQuery.data ?? [],
        cesRows: cesRowsQuery.data ?? [],
        workingPapers: workingPapersQuery.data ?? [],
      });
      downloadText('procurement_compliance_backup.json', json, 'application/json');
      show('Downloaded the compliance backup (JSON).', 'success');
    } catch (err) {
      show(
        `Could not download the backup: ${err instanceof Error ? err.message : String(err)}`,
        'error',
      );
    }
  }, [
    transactionsQuery.data,
    findingsQuery.data,
    cesRowsQuery.data,
    workingPapersQuery.data,
    show,
  ]);

  const allColumns = useMemo<Column<FindingRead>[]>(
    () => [
      {
        key: 'dataset',
        header: 'Dataset',
        sortable: true,
        accessor: (f) => txByRef.get(f.referenceNo ?? '')?.dataset ?? '',
        render: (f) => txByRef.get(f.referenceNo ?? '')?.dataset ?? '—',
      },
      {
        key: 'reference',
        header: 'Reference',
        sortable: true,
        accessor: (f) => f.referenceNo ?? '',
        render: (f) =>
          f.referenceNo ? (
            <Link to={`/transactions?ref=${encodeURIComponent(f.referenceNo)}`}>
              {f.referenceNo}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        key: 'division',
        header: 'Division',
        sortable: true,
        accessor: (f) => txByRef.get(f.referenceNo ?? '')?.division ?? '',
        render: (f) => txByRef.get(f.referenceNo ?? '')?.division ?? '—',
      },
      {
        // R3 #2: the procedure the note was captured against, in its own column.
        key: 'procedure',
        header: 'Procedure',
        sortable: true,
        accessor: (f) => splitFindingDisplay(f).procedure,
        render: (f) => {
          const { procedure } = splitFindingDisplay(f);
          return procedure ? <Truncated text={procedure} max={200} /> : '—';
        },
      },
      {
        // R3 #2: the Finding column shows the REVIEWER'S NOTE, not the procedure.
        key: 'finding',
        header: 'Finding',
        sortable: true,
        accessor: (f) => splitFindingDisplay(f).note,
        render: (f) => {
          const { note } = splitFindingDisplay(f);
          return note ? <Truncated text={note} max={360} /> : '—';
        },
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        accessor: (f) => findingStatus(f),
        render: (f) => {
          const s = findingStatus(f);
          if (!s) return '—';
          // Icon + label, decoded by the gap-status legend under the header.
          return (
            <span className="status-cluster">
              <StatusIcon value={s} size={15} />
              {s}
            </span>
          );
        },
      },
      {
        // 1a: the review state of the finding's transaction (Reviewed / Pending …),
        // the column the new "Reviewed" filter narrows on.
        key: 'reviewed',
        header: 'Review',
        sortable: true,
        accessor: (f) => deriveFindingReviewedState(txByRef.get(f.referenceNo ?? '')),
        render: (f) => deriveFindingReviewedState(txByRef.get(f.referenceNo ?? '')),
      },
      {
        key: 'route',
        header: 'Route',
        sortable: true,
        accessor: (f) => routeLabel(f.route),
        render: (f) => routeLabel(f.route) || '—',
      },
      {
        key: 'confidence',
        header: 'Confidence',
        sortable: true,
        accessor: (f) => f.confidence ?? '',
        render: (f) => f.confidence ?? '—',
      },
      {
        // Provenance, now readable: which findings came from a confirmed engine
        // proposal (so their Confidence is the engine's, not the rulebook's).
        key: 'aiAssisted',
        header: 'AI',
        sortable: true,
        accessor: (f) => (f.isAiAssisted ? 'AI' : ''),
        render: (f) =>
          f.isAiAssisted ? (
            <span className="ai-prop-tag" title={f.aiModel ? `Proposed by ${f.aiModel}` : 'AI-assisted'}>
              AI
            </span>
          ) : (
            '—'
          ),
      },
      {
        // The engine's PFMA section — the deliverable's "PFMA Impact" column,
        // previously stored but never rendered.
        key: 'pfmaImpact',
        header: 'PFMA Impact',
        sortable: true,
        accessor: (f) => f.aiPfmaSection ?? '',
        render: (f) => f.aiPfmaSection ?? '—',
      },
      {
        key: 'matchedRules',
        header: 'Matched rule theme',
        sortable: true,
        accessor: (f) => (f.matchedRules ?? []).join('; '),
        render: (f) =>
          (f.matchedRules ?? []).length > 0 ? (
            <Truncated text={(f.matchedRules ?? []).join('; ')} max={260} />
          ) : (
            '—'
          ),
      },
      {
        key: 'anchors',
        header: 'Anchors',
        accessor: (f) => (f.anchors ?? []).join('; '),
        render: (f) =>
          (f.anchors ?? []).length > 0 ? (
            <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '4px' }}>
              {(f.anchors ?? []).map((a) => (
                <Badge key={a}>{a}</Badge>
              ))}
            </span>
          ) : (
            '—'
          ),
      },
      {
        key: 'regulatoryRefs',
        header: 'Regulatory References',
        accessor: (f) => flattenRefsText(f.regulatoryRefs),
        render: (f) => {
          const text = flattenRefsText(f.regulatoryRefs);
          return text ? <Truncated text={text} max={320} /> : '—';
        },
      },
      {
        key: 'compliance',
        header: 'Compliance',
        sortable: true,
        accessor: (f) => effectiveFindingCompliance(f),
        render: (f) => (
          <ComplianceBadge level={effectiveFindingCompliance(f) || 'Pending Review'} />
        ),
      },
      {
        key: 'rationale',
        header: 'Rationale',
        sortable: true,
        accessor: (f) => f.rationale ?? '',
        render: (f) => (f.rationale ? <Truncated text={f.rationale} max={320} /> : '—'),
      },
      {
        // R3 #2 audit: findings are replaced on each save, so the insert
        // timestamp reads as "last updated". Who is in the audit trail (Users).
        key: 'updated',
        header: 'Updated',
        sortable: true,
        accessor: (f) => f.createdAt ?? '',
        render: (f) =>
          f.createdAt ? (
            <span title={new Date(f.createdAt).toLocaleString('en-ZA')}>
              {new Date(f.createdAt).toLocaleDateString('en-ZA')}
            </span>
          ) : (
            '—'
          ),
      },
    ],
    [txByRef],
  );

  // Default to the seven lead columns; the other six are one click away. Nothing
  // is removed — `allColumns` is still the source, and every export is unaffected.
  const columns = useMemo(
    () => (showAllColumns ? allColumns : allColumns.filter((c) => PRIMARY_COLUMNS.includes(c.key))),
    [allColumns, showAllColumns],
  );

  // Bound the rendered register — it runs to well over a thousand rows.
  const pagination = usePagination(filtered);

  const isLoading = findingsQuery.isLoading || transactionsQuery.isLoading;
  const error = findingsQuery.error ?? transactionsQuery.error;

  if (isLoading) return <LoadingState message="Loading findings…" />;
  if (error) {
    return (
      <ErrorState
        message="Could not load the findings register."
        detail={error instanceof Error ? error.message : String(error)}
        onRetry={() => {
          void findingsQuery.refetch();
          void transactionsQuery.refetch();
        }}
      />
    );
  }
  if (findings.length === 0) {
    return <EmptyState message="No non-compliance findings to display." />;
  }

  return (
    <>
      <FindingFilters
        filters={filters}
        onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
        onReset={() => setFilters(OUTSTANDING_FILTERS)}
        datasetOptions={datasetOptions}
        categoryOptions={DATASET_CATEGORIES}
        divisionOptions={divisionOptions}
        complianceOptions={complianceOptions}
        anchorOptions={anchorOptions}
        reviewedOptions={REVIEW_STATUS_OPTIONS}
      />

      <div className="card">
        <div className="section-head">
          <div>
            <h3>Findings Register</h3>
            <div className="small">
              {`${filtered.length.toLocaleString('en-ZA')} of ${split.identified.toLocaleString('en-ZA')} finding(s)`}
              {' · '}
              {`${split.outstanding.toLocaleString('en-ZA')} outstanding · ${split.selfDeclared.toLocaleString('en-ZA')} self-declared · ${split.resolved.toLocaleString('en-ZA')} resolved`}
            </div>
          </div>
          <div className="actions">
            <Button size="sm" onClick={exportExcel} disabled={filtered.length === 0}>
              Export Excel (XLSX)
            </Button>
            <Button size="sm" onClick={exportCsv} disabled={filtered.length === 0}>
              Export CSV
            </Button>
            <Button size="sm" onClick={exportBackup}>
              Export backup (JSON)
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowAllColumns((v) => !v)}
              aria-pressed={showAllColumns}
              title="Show every column the register carries, or just the seven lead columns"
            >
              {showAllColumns ? 'Fewer columns' : 'More columns'}
            </Button>
          </div>
        </div>
        {/* Status legend under the register header (9 Jun call): the four-state
            gap/finding lifecycle every Status cell is drawn from. */}
        <StatusLegend kind="gapStatus" />
        <DataTable
          columns={columns}
          rows={pagination.slice}
          getRowKey={(f) => f.id}
          initialSort={{ key: 'reference', dir: 'asc' }}
          emptyMessage="No findings match the filters."
        />
        <Pager pagination={pagination} noun="finding" />
      </div>
    </>
  );
}
